import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import HeaderVariant from '../common/HeaderVariant';
import { Link } from 'react-router-dom';
import Footer from '../Footer/Footer';

const REFERRAL_CODES = {
  '001': 'Wayne PCNY',
  // Add more referral codes as needed
};

const SuccessAnimation = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <motion.div
      initial={{ scale: 0.5 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.5 }}
      className="bg-white text-black p-8 rounded-lg shadow-xl text-center"
    >
      <div className="w-16 h-16 bg-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Success checkmark">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold mb-2">Registration Complete!</h3>
      <p className="text-gray-600">Thank you for registering with Jacks.</p>
      <p className="text-gray-600 mt-2">Please wait to be redirected to schedule your interview.</p>
    </motion.div>
  </motion.div>
);

const ChauffeurForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    fullAddress: '',
    apartmentSuite: '',
    city: '',
    state: '',
    zipCode: '',
    driversLicenseNumber: '',
    stateOfIssuance: '',
    licenseClassification: '',
    licenseExpirationDate: '',
    workAvailability: '',
    locationPreference: '',
    drivingExperience: '',
    militaryService: '',
    militaryBranch: '',
    lawEnforcement: '',
    lawEnforcementLocation: '',
    termsAccepted: false,
    heardAboutUs: '',
    socialMediaUsername: '',
    referralCode: ''
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get referrer name from code
      const referrerName = REFERRAL_CODES[formData.referralCode] || 'No Referrer';

      // Prepare the data for Google Sheets in exact column order
      const sheetData = [
        // A: Timestamp (added by Google Script)
        formData.firstName,            // B: First Name
        formData.lastName,             // C: Last Name
        formData.email,                // D: Email
        formData.phoneNumber,          // E: Phone Number
        formData.fullAddress,          // F: Full Address
        formData.apartmentSuite,       // G: Apartment/Suite
        formData.city,                 // H: City
        formData.state,                // I: State
        formData.zipCode,              // J: Zip Code
        formData.driversLicenseNumber, // K: Drivers License Number
        formData.stateOfIssuance,      // L: State of Issuance
        formData.licenseClassification,// M: License Classification
        formData.licenseExpirationDate,// N: License Expiration Date
        formData.workAvailability,     // O: Work Availability
        formData.locationPreference,   // P: Location Preference
        formData.drivingExperience,    // Q: Driving Experience
        formData.militaryService,      // R: Military Service
        formData.militaryBranch,       // S: Military Branch
        formData.lawEnforcement,       // T: Law Enforcement
        formData.lawEnforcementLocation,// U: Law Enforcement Location
        formData.termsAccepted ? "Yes" : "No", // V: Terms Accepted
        formData.heardAboutUs,         // W: How did you hear about us
        formData.socialMediaUsername,   // X: @ (Social Media Username/Details)
        formData.referralCode,         // Y: Referral Code
        referrerName                   // Z: Referrer Name
      ];

      // Send data to Google Sheets
      const response = await fetch('https://script.google.com/macros/s/AKfycbx4LAXMXwZJMWkwqRWjEYUAT6hQv6EuIqP6e_OMNu_Pph-Mj182hhVryu6v2AN0MMeQ/exec', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: sheetData
        })
      });

      // Send email notification
      const emailFormData = new FormData();
      emailFormData.append('_subject', 'New Chauffeur Application');
      emailFormData.append('_template', 'table');
      emailFormData.append('_captcha', 'false');
      emailFormData.append('_replyto', 'jacksfileshello@gmail.com');
      emailFormData.append('_from', 'jacksfileshello@gmail.com');
      
      // Add form fields with proper labels
      emailFormData.append('Name', `${formData.firstName} ${formData.lastName}`);
      emailFormData.append('Email', formData.email);
      emailFormData.append('Phone', formData.phoneNumber);
      emailFormData.append('Location', `${formData.fullAddress}, ${formData.city}, ${formData.state} ${formData.zipCode}`);
      emailFormData.append('Source', formData.heardAboutUs);
      if (formData.socialMediaUsername) {
        emailFormData.append('Social Media Username', formData.socialMediaUsername);
      }
      if (formData.referralCode) {
        emailFormData.append('Referral', `${formData.referralCode} (${referrerName})`);
      }

      const emailResponse = await fetch('https://formsubmit.co/jacksfileshello@gmail.com', {
        method: 'POST',
        body: emailFormData
      });

      if (!emailResponse.ok) {
        throw new Error('Email submission failed');
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        window.location.href = 'https://calendly.com/hello-jacks/interviews';
      }, 3000);

    } catch (error) {
      console.error('Submission error:', error);
      alert('An error occurred. Please try again.');
    }

    setIsSubmitting(false);
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const inputClassName = "w-full bg-[#1A1A1A] text-white px-4 py-3 rounded-md border border-gray-800 focus:border-white focus:ring-1 focus:ring-white transition-colors";

  const renderStep = () => {
    switch (currentStep) {
      case 1: // Personal Information
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Enter your first name"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Enter your last name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phoneNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 10) {
                    let formattedPhone = '';
                    if (value.length > 0) formattedPhone = `(${value.slice(0,3)}`;
                    if (value.length > 3) formattedPhone += `) ${value.slice(3,6)}`;
                    if (value.length > 6) formattedPhone += `-${value.slice(6,10)}`;
                    setFormData({ ...formData, phoneNumber: formattedPhone });
                  }
                }}
                className={inputClassName}
                placeholder="Enter your phone number"
                required
              />
            </div>

            <div>
              <label htmlFor="referralCode" className="block text-sm font-medium text-gray-300 mb-2">
                Referral Code (if any)
              </label>
              <input
                type="text"
                id="referralCode"
                name="referralCode"
                value={formData.referralCode}
                onChange={(e) => setFormData({ ...formData, referralCode: e.target.value })}
                className={inputClassName}
                placeholder="Enter referral code"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className={inputClassName}
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="fullAddress" className="block text-sm font-medium text-gray-300 mb-2">
                Full Address
              </label>
              <input
                type="text"
                name="fullAddress"
                id="fullAddress"
                value={formData.fullAddress}
                onChange={(e) => setFormData({ ...formData, fullAddress: e.target.value })}
                required
                className={inputClassName}
                placeholder="Enter your street address"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.firstName || !formData.lastName || !formData.phoneNumber || !formData.email}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(!formData.firstName || !formData.lastName || !formData.phoneNumber || !formData.email) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 2: // Address Information
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label htmlFor="fullAddress" className="block text-sm font-medium text-gray-300 mb-2">
                Full Address
              </label>
              <input
                type="text"
                name="fullAddress"
                id="fullAddress"
                value={formData.fullAddress}
                onChange={(e) => setFormData({ ...formData, fullAddress: e.target.value })}
                required
                className={inputClassName}
                placeholder="Street address"
              />
            </div>

            <div>
              <label htmlFor="apartmentSuite" className="block text-sm font-medium text-gray-300 mb-2">
                Apartment/Suite
              </label>
              <input
                type="text"
                name="apartmentSuite"
                id="apartmentSuite"
                value={formData.apartmentSuite}
                onChange={(e) => setFormData({ ...formData, apartmentSuite: e.target.value })}
                className={inputClassName}
                placeholder="Apt, Suite, Unit (optional)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-300 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Enter city"
                />
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-300 mb-2">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Enter state"
                />
              </div>

              <div>
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-300 mb-2">
                  ZIP Code
                </label>
                <input
                  type="text"
                  name="zipCode"
                  id="zipCode"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Enter ZIP code"
                />
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.fullAddress || !formData.city || !formData.state || !formData.zipCode}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(!formData.fullAddress || !formData.city || !formData.state || !formData.zipCode) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 3: // License & Experience
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="driversLicenseNumber" className="block text-sm font-medium text-gray-300 mb-2">
                  Driver's License Number
                </label>
                <input
                  type="text"
                  name="driversLicenseNumber"
                  id="driversLicenseNumber"
                  value={formData.driversLicenseNumber}
                  onChange={(e) => setFormData({ ...formData, driversLicenseNumber: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Enter license number"
                />
              </div>

              <div>
                <label htmlFor="stateOfIssuance" className="block text-sm font-medium text-gray-300 mb-2">
                  State of Issuance
                </label>
                <input
                  type="text"
                  name="stateOfIssuance"
                  id="stateOfIssuance"
                  value={formData.stateOfIssuance}
                  onChange={(e) => setFormData({ ...formData, stateOfIssuance: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Enter state"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="licenseClassification" className="block text-sm font-medium text-gray-300 mb-2">
                  License Classification
                </label>
                <select
                  name="licenseClassification"
                  id="licenseClassification"
                  value={formData.licenseClassification}
                  onChange={(e) => setFormData({ ...formData, licenseClassification: e.target.value })}
                  required
                  className={inputClassName}
                >
                  <option value="">Select Classification</option>
                  <option value="A">Class A</option>
                  <option value="B">Class B</option>
                  <option value="C">Class C</option>
                  <option value="D">Class D</option>
                </select>
              </div>

              <div>
                <label htmlFor="licenseExpirationDate" className="block text-sm font-medium text-gray-300 mb-2">
                  License Expiration Date
                </label>
                <input
                  type="date"
                  name="licenseExpirationDate"
                  id="licenseExpirationDate"
                  value={formData.licenseExpirationDate}
                  onChange={(e) => setFormData({ ...formData, licenseExpirationDate: e.target.value })}
                  required
                  className={inputClassName}
                />
              </div>
            </div>

            <div>
              <label htmlFor="workAvailability" className="block text-sm font-medium text-gray-300 mb-2">
                Work Availability
              </label>
              <input
                type="text"
                name="workAvailability"
                id="workAvailability"
                value={formData.workAvailability}
                onChange={(e) => setFormData({ ...formData, workAvailability: e.target.value })}
                required
                className={inputClassName}
                placeholder="Enter your availability"
              />
            </div>

            <div>
              <label htmlFor="locationPreference" className="block text-sm font-medium text-gray-300 mb-2">
                Location Preference
              </label>
              <input
                type="text"
                name="locationPreference"
                id="locationPreference"
                value={formData.locationPreference}
                onChange={(e) => setFormData({ ...formData, locationPreference: e.target.value })}
                required
                className={inputClassName}
                placeholder="Enter preferred work location"
              />
            </div>

            <div>
              <label htmlFor="drivingExperience" className="block text-sm font-medium text-gray-300 mb-2">
                Driving Experience
              </label>
              <textarea
                name="drivingExperience"
                id="drivingExperience"
                value={formData.drivingExperience}
                onChange={(e) => setFormData({ ...formData, drivingExperience: e.target.value })}
                required
                className={`${inputClassName} h-24`}
                placeholder="Describe your driving experience"
              />
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.driversLicenseNumber || !formData.stateOfIssuance || !formData.licenseClassification || !formData.licenseExpirationDate || !formData.workAvailability || !formData.locationPreference || !formData.drivingExperience}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(!formData.driversLicenseNumber || !formData.stateOfIssuance || !formData.licenseClassification || !formData.licenseExpirationDate || !formData.workAvailability || !formData.locationPreference || !formData.drivingExperience) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 4: // Background & Terms
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="militaryService" className="block text-sm font-medium text-gray-300 mb-2">
                  Military Service
                </label>
                <select
                  name="militaryService"
                  id="militaryService"
                  value={formData.militaryService}
                  onChange={(e) => setFormData({ ...formData, militaryService: e.target.value })}
                  required
                  className={inputClassName}
                >
                  <option value="">Select Option</option>
                  <option value="None">None</option>
                  <option value="Current">Current</option>
                  <option value="Veteran">Veteran</option>
                </select>
              </div>

              {formData.militaryService && formData.militaryService !== 'None' && (
                <div>
                  <label htmlFor="militaryBranch" className="block text-sm font-medium text-gray-300 mb-2">
                    Military Branch
                  </label>
                  <select
                    name="militaryBranch"
                    id="militaryBranch"
                    value={formData.militaryBranch}
                    onChange={(e) => setFormData({ ...formData, militaryBranch: e.target.value })}
                    required
                    className={inputClassName}
                  >
                    <option value="">Select Branch</option>
                    <option value="Army">Army</option>
                    <option value="Navy">Navy</option>
                    <option value="Air Force">Air Force</option>
                    <option value="Marines">Marines</option>
                    <option value="Coast Guard">Coast Guard</option>
                  </select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="lawEnforcement" className="block text-sm font-medium text-gray-300 mb-2">
                  Law Enforcement Experience
                </label>
                <select
                  name="lawEnforcement"
                  id="lawEnforcement"
                  value={formData.lawEnforcement}
                  onChange={(e) => setFormData({ ...formData, lawEnforcement: e.target.value })}
                  required
                  className={inputClassName}
                >
                  <option value="">Select Option</option>
                  <option value="None">None</option>
                  <option value="Current">Current</option>
                  <option value="Former">Former</option>
                </select>
              </div>

              {formData.lawEnforcement && formData.lawEnforcement !== 'None' && (
                <div>
                  <label htmlFor="lawEnforcementLocation" className="block text-sm font-medium text-gray-300 mb-2">
                    Law Enforcement Location
                  </label>
                  <input
                    type="text"
                    name="lawEnforcementLocation"
                    id="lawEnforcementLocation"
                    value={formData.lawEnforcementLocation}
                    onChange={(e) => setFormData({ ...formData, lawEnforcementLocation: e.target.value })}
                    required
                    className={inputClassName}
                    placeholder="Enter location"
                  />
                </div>
              )}
            </div>

            <div>
              <label htmlFor="heardAboutUs" className="block text-sm font-medium text-gray-300 mb-2">
                How did you hear about us?
              </label>
              <select
                name="heardAboutUs"
                id="heardAboutUs"
                value={formData.heardAboutUs}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  heardAboutUs: e.target.value,
                  socialMediaUsername: ''
                })}
                required
                className={inputClassName}
              >
                <option value="">Select an option</option>
                <option value="Indeed">Indeed</option>
                <option value="Instagram">Instagram</option>
                <option value="Facebook">Facebook</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Friend">Friend</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {formData.heardAboutUs && formData.heardAboutUs !== 'Indeed' && (
              <div>
                <label htmlFor="socialMediaUsername" className="block text-sm font-medium text-gray-300 mb-2">
                  {formData.heardAboutUs === 'Friend' ? "Friend's Name" : 
                   formData.heardAboutUs === 'Other' ? 'Please specify' :
                   'Username'}
                </label>
                <input
                  type="text"
                  name="socialMediaUsername"
                  id="socialMediaUsername"
                  value={formData.socialMediaUsername}
                  onChange={(e) => setFormData({ ...formData, socialMediaUsername: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder={formData.heardAboutUs === 'Friend' ? "Enter friend's name" :
                             formData.heardAboutUs === 'Other' ? 'Please specify' :
                             `Enter your ${formData.heardAboutUs} username`}
                />
              </div>
            )}

            <div className="border-t border-gray-800 pt-6">
              <div className="flex items-start mb-6">
                <div className="flex items-center h-5">
                  <input
                    id="termsAccepted"
                    name="termsAccepted"
                    type="checkbox"
                    required
                    checked={formData.termsAccepted}
                    onChange={(e) => setFormData({
                      ...formData,
                      termsAccepted: e.target.checked
                    })}
                    className="w-4 h-4 border border-gray-800 rounded bg-[#1A1A1A] checked:bg-white checked:border-white focus:ring-1 focus:ring-white text-black transition-colors cursor-pointer"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="termsAccepted" className="font-light text-gray-300">
                    I agree to the{' '}
                    <Link to="/terms-of-use" className="font-medium text-blue-500 hover:underline">
                      Terms and Conditions
                    </Link>{', '}
                    <Link to="/disclaimer" className="font-medium text-blue-500 hover:underline">
                      Disclaimer
                    </Link>{', '}
                    and acknowledge that my information will be used in accordance with the{' '}
                    <Link to="/privacypolicy" className="font-medium text-blue-500 hover:underline">
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.heardAboutUs || (!formData.socialMediaUsername && formData.heardAboutUs !== 'Indeed') || !formData.termsAccepted}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(isSubmitting || !formData.heardAboutUs || (!formData.socialMediaUsername && formData.heardAboutUs !== 'Indeed') || !formData.termsAccepted) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <HeaderVariant />
      
      <div className="relative min-h-screen bg-black">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] select-none pointer-events-none overflow-hidden">
          <h1 className="text-[20vw] font-bold text-white whitespace-nowrap">CHAUFFEUR</h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 container mx-auto px-4 py-24 pt-32"
        >
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 pt-24">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Chauffeur Application
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Join our network of professional chauffeurs
              </p>
            </div>

            <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8">
              <div className="flex justify-between mb-8">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`flex items-center ${step < 4 ? 'flex-1' : ''}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                        ${currentStep >= step ? 'bg-white text-black' : 'bg-gray-800 text-gray-400'}
                      `}
                    >
                      {step}
                    </div>
                    {step < 4 && (
                      <div
                        className={`flex-1 h-px mx-2 transition-colors
                          ${currentStep > step ? 'bg-white' : 'bg-gray-800'}
                        `}
                      />
                    )}
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                <AnimatePresence mode="wait">
                  {renderStep()}
                </AnimatePresence>
              </form>
            </div>
          </div>
        </motion.div>
      </div>

      <Footer />

      <AnimatePresence>
        {showSuccess && <SuccessAnimation />}
      </AnimatePresence>
    </div>
  );
};

export default ChauffeurForm; 